import type { ReactNode } from 'react'

import { AthenaError } from '../../lib/api/client.ts'
import { describeError } from '../../lib/api/messages.ts'
import { Button } from './Button.tsx'

/**
 * Os três estados que toda listagem tem além do estado com dados.
 *
 * Eles moram juntos porque a decisão que os separa é uma só: **carregando não é
 * vazio, e vazio não é erro**. Renderizar "nenhum registro" enquanto a
 * requisição está no ar faz o operador cadastrar de novo o que já existe;
 * renderizar "nenhum registro" quando o servidor devolveu 500 esconde uma falha
 * atrás de uma frase tranquilizadora.
 */

export function LoadingRow({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 px-3 py-6 text-sm text-fg-muted"
    >
      {label}
    </div>
  )
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string
  hint?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-start gap-2 px-3 py-8">
      <p className="text-base text-fg-secondary">{title}</p>
      {hint !== undefined && <p className="max-w-lg text-sm leading-snug text-fg-muted">{hint}</p>}
      {action}
    </div>
  )
}

/**
 * Falha de carga.
 *
 * O `traceId` só aparece em 5xx, e aparece porque é o que o suporte pede: sem
 * ele o operador descreve o sintoma, com ele a linha do log é encontrada. Em
 * 4xx ele não ajuda ninguém e seria ruído.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const trace = error instanceof AthenaError ? error.traceId : null
  const forbidden = error instanceof AthenaError && error.status === 403

  return (
    <div role="alert" className="flex flex-col items-start gap-2 border-l-2 border-crit px-3 py-4">
      <p className="text-base text-crit">{describeError(error)}</p>

      {trace !== null && (
        <p className="text-sm text-fg-muted">
          Informe ao suporte o traceId <span className="font-mono text-fg-secondary">{trace}</span>
        </p>
      )}

      {/* Recarregar só é oferecido onde repetir é seguro: leitura. Um 409 de
          escrita nunca ganha botão de tentar de novo — a intenção precisa ser
          reformulada, não repetida. */}
      {onRetry !== undefined && !forbidden && (
        <Button size="sm" onClick={onRetry}>
          Tentar de novo
        </Button>
      )}
    </div>
  )
}

/** Aviso inline dentro de um formulário ou painel. */
export function Notice({
  tone = 'warn',
  children,
}: {
  tone?: 'warn' | 'crit' | 'neutral'
  children: ReactNode
}) {
  const chrome =
    tone === 'crit'
      ? 'border-crit text-crit'
      : tone === 'warn'
        ? 'border-warn text-warn'
        : 'border-line-strong text-fg-secondary'

  return (
    <p className={`border-l-2 px-2 py-1 text-sm leading-snug ${chrome}`}>{children}</p>
  )
}
